import { useContext, useState, KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { formatInTimeZone } from 'date-fns-tz'

import { RouteDetails, RouteDetailsDisruption } from '@/types/routeDetails'
import { Input } from '@/ui/Input'
import { useUpdateDisruptionMitigation } from '../../api/useUpdateDisruptionMitigation'
import { useWorkstationSelect } from '@/modules/Workstation/hooks/useWorkstationSelect'
import { useModal } from '@/components/Modal'
import { ROUTES } from '@/router'
import { useDateStore } from '@/components/DateInput'
import { RouteDetailsContext } from '../context'

import styles from './DisruptionsList.module.scss'

import ChevronRightIcon from '../../assets/chevron-right.svg'
import WarningIcon from '../../assets/warning.svg'

const Disruption = ({ id, reference, location, routes }: RouteDetailsDisruption) => {
    const { date } = useDateStore()
    const disruptionInfo = routes[0] ? routes[0] : null
    const [mitigation, setMitigation] = useState(disruptionInfo?.diversion || '')
    const { handleUpdateDisruptionMitigation } = useUpdateDisruptionMitigation(disruptionInfo?.id || id)
    const { handleWorkstationChange } = useWorkstationSelect()
    const { closeModal } = useModal()
    const { workstationNumber, readOnly } = useContext(RouteDetailsContext)

    const handleViewDisruption = () => {
        if (!workstationNumber) {
            return
        }

        handleWorkstationChange(workstationNumber)
        closeModal()
    }

    const handleUpdateDisruptionDiversion = () => {
        handleUpdateDisruptionMitigation(mitigation)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            handleUpdateDisruptionDiversion()
        }
    }

    if (!disruptionInfo) {
        return null
    }

    return (
        <div key={id} className={styles.itemContainer}>
            <Link
                to={{
                    pathname: ROUTES.DISRUPTIONS,
                    search: `?id=${id}&date=${formatInTimeZone(date, 'UTC', 'yyyy-MM-dd')}`,
                }}
                className={styles.itemInformation}
                onClick={handleViewDisruption}
            >
                <div className={styles.reference}>
                    <WarningIcon />
                    {reference}
                </div>
                <p className={styles.label}>{location}</p>
                <ChevronRightIcon />
            </Link>
            <Input
                value={mitigation}
                placeholder="Add mitigation"
                fullWidth
                transparent
                className={styles.input}
                iconClassName={styles.clearIcon}
                allowClear
                disabled={readOnly}
                onKeyDown={handleKeyDown}
                onChange={(event) => setMitigation(event.target.value)}
                onBlur={() => {
                    setTimeout(() => {
                        handleUpdateDisruptionDiversion()
                    }, 500)
                }}
            />
        </div>
    )
}

export const DisruptionsList = ({ disruptions }: Pick<RouteDetails, 'disruptions'>) => {
    if (disruptions.length === 0) {
        return null
    }

    return (
        <div className={styles.root}>
            <h4 className={styles.head}>Disruptions</h4>
            <div className={styles.list}>
                {disruptions.map((item) => (
                    <Disruption key={item.id} {...item} />
                ))}
            </div>
        </div>
    )
}
